import * as React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormGroup, FormControlLabel, Switch, Typography, Divider, Box
} from '@mui/material';

type CookiePrefs = {
  necessary: true; // always on
  analytics: boolean;
};

const STORAGE_KEY = 'cookiePrefs';

function loadPrefs(): CookiePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { necessary: true, analytics: !!JSON.parse(raw).analytics };
  } catch {}
  return { necessary: true, analytics: false };
}

function savePrefs(prefs: CookiePrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  window.dispatchEvent(new CustomEvent('cookie-prefs-changed', { detail: prefs }));
}

export default function CookiePreferencesDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const [prefs, setPrefs] = React.useState<CookiePrefs>(loadPrefs());

  React.useEffect(() => {
    if (open) setPrefs(loadPrefs());
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>🍪 Cookie Settings</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.5 }}>
          We respect your privacy. Choose which cookies you're comfortable with to help us improve your AppConfig² experience.
        </Typography>
        
        <FormGroup sx={{ gap: 2 }}>
          <Box>
            <FormControlLabel 
              control={<Switch checked disabled />} 
              label={
                <Box>
                  <Typography variant="body2" fontWeight="500">Essential cookies</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Required for core functionality like authentication and navigation. Always enabled.
                  </Typography>
                </Box>
              }
            />
          </Box>
          
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="500">Analytics cookies</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Help us understand how you use AppConfig² to improve the experience. No personal data is collected.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </FormGroup>
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💾 Your preferences are saved locally in this browser and can be changed anytime.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            savePrefs(prefs);
            onClose();
          }}
          sx={{ minWidth: 100 }}
        >
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
}